import {
  type BaseQueryFn,
  createApi,
  retry,
} from '@reduxjs/toolkit/query/react'
import { type AxiosError, type AxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import config from '@/config'
import {
  type ServiceCursorPaginatedResponseType,
  ServiceErrorHandler,
} from '@/connect-types/backend/service'
import {
  type RewardVoucherCsvUploadRequestType,
  type RewardVoucherCsvUploadResponseType,
  type RewardVoucherSignedCsvUrlRequestType,
  type RewardVoucherSignedCsvUrlResponseType,
} from '@/connect-types/loyalty/reward-voucher-csv.type'
import { customAxios } from '@/utils/axiosHelper'
import { buildUrl } from '@/utils/common'
import { shouldBailout } from '../helpers'
import { type AutomationConfig } from './types/automation.type'
import { type Brandkit } from './types/brandkit.type'
import { type Gift } from './types/gift.type'
import { type InteractionHistory } from './types/interaction-history.type'
import {
  type LoyaltyScheme,
  type LoyaltyScheme_LoyaltyProfiles,
} from './types/loyalty-scheme.type'
import { type LoyaltySchemeListItem } from './types/loyalty-scheme-list-item.type'
import { type LoyaltyScheme_UserProfile } from './types/loyalty-scheme_user-profile.type'
import {
  type PointsForSpendRuleInput,
  type PointsForSpendRulesResponse,
} from './types/points-for-spend-rule.type'
import {
  type ApplyPointsCatchupBody,
  type CatchupUndoResult,
  type PointsCatchupBatch,
  type PointsCatchupJobStarted,
  type PointsCatchupScope,
} from './types/points-catchup.type'
import { type Reward, type RewardSquareEntity } from './types/reward.type'
import { type Staff } from './types/staff.type'
import { type Stats } from './types/stats.type'
import { type User_Reward } from './types/user_reward.type'
import { type VoucherCsv } from './types/voucher-csv.type'
import {
  type MemberImport,
  type MemberImportDownload,
  type MemberImportPointsMode,
  type MemberImportRow,
  type MemberImportRowStatus,
  type MemberImportWithUpload,
  type OffsetPaginated,
} from './types/member-import.type'
import {
  type OrganisationReportRequest,
  type OrganisationReportResponse,
} from './types/organisation-report.type'
import { type LoyaltySchemePayload } from './validations/loyalty-scheme'
import { type CreateReward, type UpdateReward } from './validations/reward'

interface InteractionSummaryStats {
  total: number
  count: number
  averagePerTransaction?: number
  averagePointsPerRedemption?: number
  averagePerReferral?: number
  averagePointsPerVoucher?: number
}

interface RewardTypeCount {
  title: string
  count: number
}

interface LoyaltySchemeInteractionStats {
  summary: {
    pointsIssued: InteractionSummaryStats
    rewardsRedeemed: InteractionSummaryStats
    referralPoints: InteractionSummaryStats
    vouchersRedeemed: InteractionSummaryStats
  }
  details: {
    rewards: {
      byType: RewardTypeCount[]
    }
    vouchers: {
      byReward: RewardTypeCount[]
    }
  }
  dateRange: {
    from: string
    to: string
  }
}
const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
    headers?: AxiosRequestConfig['headers']
    timeout?: AxiosRequestConfig['timeout']
  }> =>
  async ({ url, method, data, params, headers, timeout }) => {
    try {
      let axiosUrl = baseUrl + url
      if (url.startsWith('https://')) {
        axiosUrl = url
      }

      const result = await customAxios({
        url: axiosUrl,
        method,
        data,
        params,
        headers,
        timeout,
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError
      console.log(err, 'error-here')

      if (shouldBailout(err)) {
        retry.fail(err)
      }

      if (
        method !== 'get' &&
        err.response?.status !== 401 &&
        err.response?.status !== 404 &&
        !url.includes('segment') &&
        !url.includes('oauth')
      ) {
        ServiceErrorHandler(err)
      }

      //
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

const loyaltyApi = createApi({
  reducerPath: 'loyalty',
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.loyalty }), {
    maxRetries: 2,
  }),
  tagTypes: [
    'Loyalty',
    'Reward',
    'User_Reward',
    'Automations',
    'LoyaltyUser',
    'Card',
    'PointsForSpendRules',
    'MemberImport',
    'PointsCatchup',
  ],
  endpoints: (build) => ({
    getLoyaltySchemes: build.query<LoyaltyScheme[], { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/v1/organisation/${orgId}/loyalty-schemes`,
        method: 'get',
      }),
      providesTags: (result, _error, { orgId }) => [
        { type: 'Loyalty', id: orgId },
      ],
    }),
    /**
     * Slim counterpart to `getLoyaltySchemes` — id, name, live flag, org-wide
     * flag and plain serial strings. Prefer this anywhere the list is only being
     * turned into options or links; the fat endpoint drags brand kits, user
     * profiles and nested serial join rows with it.
     */
    getLoyaltySchemesList: build.query<
      LoyaltySchemeListItem[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/v1/organisation/${orgId}/loyalty-schemes/list`,
        method: 'get',
      }),
      // The org tag is what the scheme mutations already invalidate, so this
      // list refreshes with the fat one without touching them.
      providesTags: (items = [], _error, { orgId }) => [
        ...items.map((item) => ({ id: item.id, type: 'Loyalty' as const })),
        { type: 'Loyalty', id: orgId },
      ],
    }),
    getLoyaltyScheme: build.query<LoyaltyScheme, { loyaltySchemeId: string }>({
      query: ({ loyaltySchemeId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    createLoyaltyScheme: build.mutation<
      LoyaltyScheme,
      { orgId: string; data: LoyaltySchemePayload }
    >({
      query: ({ orgId, data }) => ({
        url: `/v1/organisation/${orgId}/loyalty-schemes`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (data, error, { orgId }) => {
        // Only invalidate tags on successful response
        if (error) {
          return []
        }
        return [{ type: 'Loyalty', id: orgId }]
      },
    }),
    updateLoyaltyScheme: build.mutation<
      LoyaltyScheme,
      { loyaltySchemeId: string; data: LoyaltySchemePayload }
    >({
      query: ({ loyaltySchemeId, data }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    /**
     * The scheme's tiered points-for-spend rules, ascending by `minSpend`.
     * Thresholds are pence; a scheme with no rules falls back to the
     * proportional pair stored on the scheme itself.
     */
    getPointsForSpendRules: build.query<
      PointsForSpendRulesResponse,
      { loyaltySchemeId: string }
    >({
      query: ({ loyaltySchemeId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/points-for-spend-rules`,
        method: 'get',
      }),
      providesTags: (_result, _error, { loyaltySchemeId }) => [
        { type: 'PointsForSpendRules', id: loyaltySchemeId },
      ],
    }),
    /**
     * Replaces the whole rule set in one shot. There is no per-rule endpoint:
     * the ids are the service's, so the client always sends the full set it
     * wants to be live (an empty array clears the tiers).
     */
    replacePointsForSpendRules: build.mutation<
      PointsForSpendRulesResponse,
      { loyaltySchemeId: string; rules: PointsForSpendRuleInput[] }
    >({
      query: ({ loyaltySchemeId, rules }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/points-for-spend-rules`,
        method: 'PUT',
        data: { rules },
      }),
      invalidatesTags: (_result, error, { loyaltySchemeId }) => {
        if (error) {
          return []
        }
        return [
          { type: 'PointsForSpendRules', id: loyaltySchemeId },
          { type: 'Loyalty', id: loyaltySchemeId },
        ]
      },
    }),
    getBrandkit: build.query<Brandkit, { orgId: string; brandkitId: string }>({
      query: ({ orgId, brandkitId }) => ({
        url: `/v1/organisations/${orgId}/brandkit/${brandkitId}`,
        method: 'GET',
      }),
    }),
    deleteLoyaltyScheme: build.mutation<
      LoyaltyScheme,
      { loyaltySchemeId: string }
    >({
      query: ({ loyaltySchemeId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
        { type: 'Loyalty', id: data?.orgId },
      ],
    }),
    getLoyaltySchemeUserProfiles: build.query<
      ServiceCursorPaginatedResponseType<LoyaltyScheme_UserProfile>,
      {
        loyaltySchemeId: string
        fromDate?: string
        toDate?: string
        query?: { cursor: string; limit: number; search?: string }
      }
    >({
      query: ({
        loyaltySchemeId,
        fromDate,
        toDate,
        query = {
          cursor: '',
          limit: 25,
          search: '',
        },
      }) => ({
        //TODO: Rename this endpoint as it is not right
        url: buildUrl(`/v1/loyalty-schemes/${loyaltySchemeId}/user-profiles`, {
          ...query,
          toDate,
          fromDate,
        }),
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    getLoyaltySchemeUserProfile: build.query<
      LoyaltyScheme_UserProfile,
      { loyaltySchemeId: string; loyaltySchemeUserProfileId: string }
    >({
      extraOptions: {
        retry: 0,
      },
      query: ({ loyaltySchemeId, loyaltySchemeUserProfileId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/loyalty-scheme-user-profiles/${loyaltySchemeUserProfileId}`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeUserProfileId }) => [
        { type: 'LoyaltyUser', id: loyaltySchemeUserProfileId },
      ],
    }),
    getReward: build.query<
      Reward,
      { rewardId: string; loyaltySchemeId: string }
    >({
      query: ({ rewardId, loyaltySchemeId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/${rewardId}`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId, rewardId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
        { type: 'Reward', id: rewardId },
      ],
    }),
    getRewards: build.query<
      ServiceCursorPaginatedResponseType<Reward>,
      {
        loyaltySchemeId: string
        fromDate?: string
        toDate?: string
        query?: {
          cursor: string
          limit: number
          search?: string
          archived?: boolean
        }
      }
    >({
      query: ({
        loyaltySchemeId,
        fromDate,
        toDate,
        query = {
          cursor: '',
          limit: 25,
          search: '',
          archived: false,
        },
      }) => ({
        url: buildUrl(`/v1/loyalty-schemes/${loyaltySchemeId}/rewards`, {
          ...query,
          toDate,
          fromDate,
        }),
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    createReward: build.mutation<
      Reward,
      { loyaltySchemeId: string; data: CreateReward }
    >({
      query: ({ loyaltySchemeId, data }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    deleteReward: build.mutation<
      Reward,
      { loyaltySchemeId: string; rewardId: string }
    >({
      query: ({ loyaltySchemeId, rewardId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/${rewardId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    updateReward: build.mutation<
      Reward,
      { loyaltySchemeId: string; rewardId: string; data: UpdateReward }
    >({
      query: ({ loyaltySchemeId, rewardId, data }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/${rewardId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId, rewardId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
        { type: 'Reward', id: rewardId },
      ],
    }),
    listSquareEntitiesForReward: build.query<
      { entities: RewardSquareEntity[] },
      { loyaltySchemeId: string; rewardId: string }
    >({
      query: ({ loyaltySchemeId, rewardId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/${rewardId}/square-entities`,
        method: 'get',
      }),
      providesTags: (_result, _error, { rewardId }) => [
        { type: 'Reward', id: rewardId },
      ],
    }),
    createSquareGroupForRewardEntity: build.mutation<
      { integrationId: string; squareCustomerGroupId: string },
      { loyaltySchemeId: string; rewardId: string; integrationId: string }
    >({
      query: ({ loyaltySchemeId, rewardId, integrationId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/${rewardId}/square-entities/${integrationId}`,
        method: 'POST',
      }),
      invalidatesTags: (_data, _error, { rewardId }) => [
        { type: 'Reward', id: rewardId },
      ],
    }),
    getInteractionHistory: build.query<
      ServiceCursorPaginatedResponseType<InteractionHistory>,
      {
        loyaltySchemeId: string
        fromDate?: string
        toDate?: string
        query?: {
          cursor: string
          limit: number
          loyaltySchemeUserProfileId?: string | null | undefined
        }
      }
    >({
      query: ({
        loyaltySchemeId,
        fromDate,
        toDate,
        query = {
          cursor: '',
          limit: 25,
          loyaltySchemeUserProfileId: null,
        },
      }) => ({
        url: buildUrl(
          `/v1/loyalty-schemes/${loyaltySchemeId}/interaction-history`,
          { ...query, toDate, fromDate }
        ),
        method: 'GET',
      }),
    }),
    getUserReward: build.query<
      User_Reward,
      { loyaltySchemeId: string; userRewardId: string }
    >({
      query: ({ loyaltySchemeId, userRewardId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/user-rewards/${userRewardId}`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId, userRewardId }) => [
        { type: 'User_Reward', id: userRewardId },
      ],
    }),
    getUserRewardByQrCodeData: build.query<
      User_Reward,
      { loyaltySchemeId: string; encryptedQrCodeData: string }
    >({
      query: ({ loyaltySchemeId, encryptedQrCodeData }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/qr-code-data/${encryptedQrCodeData}`,
        method: 'get',
      }),
      /*
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
      */
    }),
    redeemRewardVia: build.query<
      User_Reward,
      {
        loyaltySchemeId: string
        encryptedQrCodeData: string
        serialId: string | null | undefined
        organisationId: string
      }
    >({
      keepUnusedDataFor: 0,
      query: ({
        loyaltySchemeId,
        encryptedQrCodeData,
        serialId,
        organisationId,
      }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/reward/redeem`,
        method: 'post',
        data: { encryptedQrCodeData, serialId, organisationId },
      }),

      transformErrorResponse(error: any) {
        const errorMessage =
          error?.response?.data?.message || 'Failed to redeem reward'
        // toast.error(errorMessage)
        return errorMessage
      },
    }),
    redeemReward: build.mutation<
      User_Reward,
      {
        loyaltySchemeId: string
        encryptedQrCodeData: string
        serialId: string | null | undefined
        organisationId: string
      }
    >({
      query: ({
        loyaltySchemeId,
        encryptedQrCodeData,
        serialId,
        organisationId,
      }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/reward/redeem`,
        method: 'post',
        data: { encryptedQrCodeData, serialId, organisationId },
      }),
      invalidatesTags: (item: any) => [
        { type: 'LoyaltyUser', id: item?.loyaltySchemeUserProfileId || '' },
      ],
      transformErrorResponse(error: any) {
        const errorMessage =
          error?.response?.data?.message || 'Failed to redeem reward'
        // toast.error(errorMessage)
        return errorMessage
      },
    }),
    redeemRewards: build.mutation<
      User_Reward[],
      {
        loyaltySchemeId: string
        loyaltySchemeUserProfileId: string
        rewards: {
          rewardId: string
          quantity: number
        }[]
        organisationId: string
        serialId: string | null | undefined
      }
    >({
      query: ({
        loyaltySchemeId,
        loyaltySchemeUserProfileId,
        rewards,
        serialId,
        organisationId,
      }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/redeem`,
        method: 'POST',
        data: { rewards, loyaltySchemeUserProfileId, serialId, organisationId },
      }),
      invalidatesTags: (item, _, { loyaltySchemeUserProfileId }) => [
        { type: 'LoyaltyUser', id: loyaltySchemeUserProfileId ?? '' },
      ],

      transformErrorResponse(error: any) {
        const errorMessage =
          error?.response?.data?.message || 'Failed to redeem rewards'
        // toast.error('Failed to redeem rewards')
        return errorMessage
      },
    }),
    addPoints: build.mutation<
      LoyaltyScheme_UserProfile,
      {
        loyaltySchemeId: string
        loyaltySchemeUserProfileId: string
        points: number
        serialId: string | null | undefined
        organisationId: string
      }
    >({
      query: ({
        loyaltySchemeId,
        loyaltySchemeUserProfileId,
        points,
        serialId,
        organisationId,
      }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/loyalty-scheme-user-profiles/${loyaltySchemeUserProfileId}/add-points/${points}`,
        method: 'post',
        data: { points, serialId, organisationId },
      }),
      invalidatesTags: (
        item,
        _,
        { loyaltySchemeUserProfileId, organisationId }
      ) => [
        { type: 'LoyaltyUser', id: loyaltySchemeUserProfileId ?? '' },
        { type: 'Card', id: organisationId },
      ],

      /*
      invalidatesTags: (
        data,
        _error,
        { loyaltySchemeId, loyaltySchemeUserProfileId, organisationId }
      ) => [
          { type: 'Loyalty', id: loyaltySchemeUserProfileId },
        ],
        */

      transformErrorResponse(error: any) {
        const errorMessage =
          error?.response?.data?.message || 'Failed to add points'
        // toast.error(errorMessage)
        return errorMessage
      },
    }),
    getStaff: build.query<Staff[], { loyaltySchemeId: string }>({
      query: ({ loyaltySchemeId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/staff`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),

    getAutomations: build.query<
      AutomationConfig[],
      { loyaltySchemeId: string }
    >({
      query: ({ loyaltySchemeId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/automations`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Automations', id: loyaltySchemeId },
      ],
    }),
    modifyAutomation: build.mutation<
      AutomationConfig[],
      {
        loyaltySchemeId: string
        automation_id: string
        data: Partial<AutomationConfig>
      }
    >({
      query: ({ loyaltySchemeId, automation_id, data }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/automations/${automation_id}`,
        method: 'put',
        data,
      }),
      invalidatesTags: (result, error, { loyaltySchemeId }) => [
        { type: 'Automations', id: loyaltySchemeId },
      ],
    }),
    getBookings_SerialSchemesForUser: build.query<
      LoyaltyScheme_LoyaltyProfiles[],
      { org_id: string; serial: string; emails: string[] }
    >({
      query: ({ org_id, serial, emails }) => ({
        url: `/v1/bookings/loyalty-schemes/${org_id}/${serial}`,
        method: 'post',
        data: {
          emails,
        },
      }),
      providesTags: (result, _error, { org_id }) => [
        { type: 'Card', id: org_id },
      ],
    }),
    createRewardVoucherCsvSignedUrl: build.mutation<
      RewardVoucherSignedCsvUrlResponseType,
      RewardVoucherSignedCsvUrlRequestType
    >({
      query: ({
        orgId,
        loyaltySchemeId,
        originalFileName = new Date().toTimeString(),
        rewardIds,
      }) => ({
        method: 'POST',
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/voucher-csv-upload`,
        data: {
          originalFileName,
          organisationId: orgId,
          rewardIds,
        },
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    createRewardVoucherCsvUpload: build.mutation<
      RewardVoucherCsvUploadResponseType,
      RewardVoucherCsvUploadRequestType
    >({
      query: ({ url, data }) => {
        console.log({ url, data })
        return {
          method: 'put',
          url,
          headers: {
            'Content-Type': 'text/csv',
          },
          timeout: 1000 * 60,
          data,
        }
      },
      invalidatesTags: (data, _error, { orgId }) => [
        { type: 'Loyalty', id: orgId },
      ],
      transformResponse(data: RewardVoucherCsvUploadResponseType) {
        toast.success('Csv uploaded for processing')
        return data
      },
      transformErrorResponse: (error: any) => {
        console.log({ error })
        toast.warning('Something went wrong')
        return error
      },
    }),
    getRewardVoucherCsvs: build.query<
      ServiceCursorPaginatedResponseType<VoucherCsv>,
      {
        loyaltySchemeId: string
        query?: {
          cursor: string
          limit: number
          search?: string
          archived?: boolean
        }
      }
    >({
      query: ({
        loyaltySchemeId,
        query = {
          cursor: '',
          limit: 25,
          search: '',
          archived: false,
        },
      }) => ({
        url: buildUrl(
          `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/voucher-csvs`,
          {
            ...query,
          }
        ),
        method: 'GET',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    updateRewardVoucherCsv: build.mutation<
      VoucherCsv,
      {
        loyaltySchemeId: string
        voucherCsvId: string
        state?: string
        deletedAt?: string
      }
    >({
      query: ({ loyaltySchemeId, voucherCsvId, state, deletedAt }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/rewards/voucher-csvs/${voucherCsvId}`,
        method: 'PUT',
        data: { state, deletedAt },
      }),
      invalidatesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
      transformResponse(data: VoucherCsv) {
        if (data?.deletedAt) {
          toast.success('Voucher csv deleted')
        } else {
          toast.success('Voucher csv updated')
        }
        return data
      },
      transformErrorResponse: (error: any) => {
        toast.warning('Something went wrong')
        return error
      },
    }),
    getStats: build.query<Stats, { loyaltySchemeId: string }>({
      query: ({ loyaltySchemeId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/stats`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    getGift: build.query<Gift, { loyaltySchemeId: string; giftId: string }>({
      query: ({ loyaltySchemeId, giftId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/gifts/${giftId}`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    getGifts: build.query<
      ServiceCursorPaginatedResponseType<Gift>,
      {
        loyaltySchemeId: string
        fromDate?: string
        toDate?: string
        query?: {
          cursor: string
          limit: number
          search?: string
          archived?: boolean
        }
      }
    >({
      query: ({
        loyaltySchemeId,
        fromDate,
        toDate,
        query = {
          cursor: '',
          limit: 25,
          search: '',
          archived: false,
        },
      }) => ({
        url: buildUrl(`/v1/loyalty-schemes/${loyaltySchemeId}/gifts`, {
          ...query,
          toDate,
          fromDate,
        }),
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    deleteGift: build.mutation<
      Gift,
      { loyaltySchemeId: string; giftId: string }
    >({
      query: ({ loyaltySchemeId, giftId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/gifts/${giftId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    createGift: build.mutation<Gift, { loyaltySchemeId: string; data: Gift }>({
      query: ({ loyaltySchemeId, data }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/gifts`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    updateGift: build.mutation<
      Gift,
      { loyaltySchemeId: string; giftId: string; data: Gift }
    >({
      query: ({ loyaltySchemeId, giftId, data }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/gifts/${giftId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (data, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    getLoyaltySchemeStats: build.query<
      {
        totalMembers: number
        activeMembers: number
        totalPointsIssued: number
      },
      { loyaltySchemeId: string }
    >({
      query: ({ loyaltySchemeId }) => ({
        url: `/v1/reports/${loyaltySchemeId}/stats`,
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    getLoyaltySchemeRewardStats: build.query<
      LoyaltySchemeInteractionStats,
      {
        loyaltySchemeId: string
        startDate?: string
        endDate?: string
      }
    >({
      query: ({ loyaltySchemeId, startDate, endDate }) => ({
        url: buildUrl(`/v1/reports/${loyaltySchemeId}/rewards/stats`, {
          startDate,
          endDate,
        }),
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    getOrganisationStats: build.query<
      {
        dailyStats: {
          date: string
          signUps: number
          rewardsRedeemed: number
          pointsRewarded: number
        }[]
        totals: {
          totalSignUps: number
          appleDevices: number
          androidDevices: number
          totalSchemes: number
        }
      },
      {
        orgId: string
        fromDate: string
        toDate: string
        loyaltySchemeId?: string
      }
    >({
      query: ({ orgId, fromDate, toDate, loyaltySchemeId }) => {
        const params = new URLSearchParams({ fromDate, toDate })
        if (loyaltySchemeId) params.append('loyaltySchemeId', loyaltySchemeId)
        return {
          url: `/v1/organisations/${orgId}/stats?${params.toString()}`,
          method: 'get',
        }
      },
      providesTags: (result, _error, { orgId, loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId ?? orgId },
      ],
    }),
    getSchemeGiftCards: build.query<
      {
        data: {
          id: string
          createdAt: string
          gan: string | null
          balanceAmount: number
          currency: string
          pointsRedeemed: number
          state: string
          isGift: boolean
          recipientEmail: string | null
          recipientName: string | null
          squareGiftCardId: string
          user: {
            id: string
            email: string
            firstName: string
            lastName: string
          }
          integrationName: string | null
        }[]
        pagination: {
          total: number
          page: number
          limit: number
          totalPages: number
        }
        totals: {
          totalCards: number
          totalPointsRedeemed: number
          totalValueIssued: number
        }
      },
      { loyaltySchemeId: string; page?: number; limit?: number }
    >({
      query: ({ loyaltySchemeId, page = 1, limit = 20 }) => ({
        url: buildUrl(`/v1/loyalty-schemes/${loyaltySchemeId}/gift-cards`, {
          page,
          limit,
        }),
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId },
      ],
    }),
    getOrganisationReports: build.query<
      OrganisationReportResponse,
      OrganisationReportRequest
    >({
      query: ({ orgId, fromDate, toDate, loyaltySchemeId, page, limit }) => ({
        url: buildUrl(`/v1/reports/organisations/${orgId}`, {
          fromDate,
          toDate,
          loyaltySchemeId,
          page,
          limit,
        }),
        method: 'get',
      }),
      providesTags: (result, _error, { orgId, loyaltySchemeId }) => [
        { type: 'Loyalty', id: loyaltySchemeId ?? orgId },
      ],
    }),

    // ── Member imports (CSV of members from another loyalty system) ────────
    getMemberImports: build.query<
      OffsetPaginated<MemberImport>,
      { loyaltySchemeId: string; page?: number; limit?: number }
    >({
      query: ({ loyaltySchemeId, page = 1, limit = 20 }) => ({
        url: buildUrl(`/v1/loyalty-schemes/${loyaltySchemeId}/member-imports`, {
          page,
          limit,
        }),
        method: 'get',
      }),
      providesTags: (result, _error, { loyaltySchemeId }) => [
        { type: 'MemberImport', id: `list-${loyaltySchemeId}` },
        ...(result?.items ?? []).map((item) => ({
          type: 'MemberImport' as const,
          id: item.id,
        })),
      ],
    }),
    getMemberImport: build.query<
      MemberImport,
      { loyaltySchemeId: string; memberImportId: string }
    >({
      query: ({ loyaltySchemeId, memberImportId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports/${memberImportId}`,
        method: 'get',
      }),
      providesTags: (_result, _error, { memberImportId }) => [
        { type: 'MemberImport', id: memberImportId },
      ],
    }),
    getMemberImportRows: build.query<
      OffsetPaginated<MemberImportRow>,
      {
        loyaltySchemeId: string
        memberImportId: string
        page?: number
        limit?: number
        status?: MemberImportRowStatus
      }
    >({
      query: ({
        loyaltySchemeId,
        memberImportId,
        page = 1,
        limit = 50,
        status,
      }) => ({
        url: buildUrl(
          `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports/${memberImportId}/rows`,
          { page, limit, ...(status ? { status } : {}) }
        ),
        method: 'get',
      }),
      providesTags: (_result, _error, { memberImportId }) => [
        { type: 'MemberImport', id: `rows-${memberImportId}` },
      ],
    }),
    /**
     * Step 1: register the job. The API answers with a presigned S3 PUT; the
     * file goes straight to the bucket with `uploadMemberImportFile`, then
     * `markMemberImportUploaded` tells the API to read and validate it.
     */
    createMemberImport: build.mutation<
      MemberImportWithUpload,
      { loyaltySchemeId: string; fileName: string; fileSize?: number }
    >({
      query: ({ loyaltySchemeId, fileName, fileSize }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports`,
        method: 'post',
        data: { fileName, fileSize },
      }),
      invalidatesTags: (_result, _error, { loyaltySchemeId }) => [
        { type: 'MemberImport', id: `list-${loyaltySchemeId}` },
      ],
    }),
    /** A fresh presigned PUT for a job whose first link expired or whose upload failed. */
    reissueMemberImportUploadUrl: build.mutation<
      MemberImportWithUpload,
      { loyaltySchemeId: string; memberImportId: string }
    >({
      query: ({ loyaltySchemeId, memberImportId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports/${memberImportId}/upload-url`,
        method: 'post',
      }),
    }),
    /**
     * The PUT to S3. No bearer token (the axios interceptor skips S3 URLs) and
     * the Content-Type has to match what was signed.
     */
    uploadMemberImportFile: build.mutation<
      void,
      { url: string; headers: Record<string, string>; file: File }
    >({
      query: ({ url, headers, file }) => ({
        url,
        method: 'put',
        headers,
        data: file,
        timeout: 1000 * 60 * 5,
      }),
    }),
    /** Step 2: the file is in the bucket, read and validate it. */
    markMemberImportUploaded: build.mutation<
      MemberImport,
      { loyaltySchemeId: string; memberImportId: string }
    >({
      query: ({ loyaltySchemeId, memberImportId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports/${memberImportId}/uploaded`,
        method: 'post',
        timeout: 1000 * 60 * 2,
      }),
      invalidatesTags: (
        _result,
        _error,
        { loyaltySchemeId, memberImportId }
      ) => [
        { type: 'MemberImport', id: memberImportId },
        { type: 'MemberImport', id: `rows-${memberImportId}` },
        { type: 'MemberImport', id: `list-${loyaltySchemeId}` },
      ],
    }),
    /** Short-lived link to the file as it was uploaded. */
    getMemberImportDownloadUrl: build.query<
      MemberImportDownload,
      { loyaltySchemeId: string; memberImportId: string }
    >({
      query: ({ loyaltySchemeId, memberImportId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports/${memberImportId}/download`,
        method: 'get',
      }),
      // Signed links expire; never serve one from cache.
      keepUnusedDataFor: 0,
    }),
    startMemberImport: build.mutation<
      MemberImport,
      {
        loyaltySchemeId: string
        memberImportId: string
        pointsMode: MemberImportPointsMode
        sendEmails: boolean
      }
    >({
      query: ({ loyaltySchemeId, memberImportId, pointsMode, sendEmails }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports/${memberImportId}/start`,
        method: 'post',
        data: { pointsMode, sendEmails },
      }),
      invalidatesTags: (
        _result,
        _error,
        { loyaltySchemeId, memberImportId }
      ) => [
        { type: 'MemberImport', id: memberImportId },
        { type: 'MemberImport', id: `rows-${memberImportId}` },
        { type: 'MemberImport', id: `list-${loyaltySchemeId}` },
      ],
    }),
    cancelMemberImport: build.mutation<
      MemberImport,
      { loyaltySchemeId: string; memberImportId: string }
    >({
      query: ({ loyaltySchemeId, memberImportId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports/${memberImportId}/cancel`,
        method: 'post',
      }),
      invalidatesTags: (
        _result,
        _error,
        { loyaltySchemeId, memberImportId }
      ) => [
        { type: 'MemberImport', id: memberImportId },
        { type: 'MemberImport', id: `list-${loyaltySchemeId}` },
      ],
    }),
    deleteMemberImport: build.mutation<
      { id: string; deleted: boolean },
      { loyaltySchemeId: string; memberImportId: string }
    >({
      query: ({ loyaltySchemeId, memberImportId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/member-imports/${memberImportId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { loyaltySchemeId }) => [
        { type: 'MemberImport', id: `list-${loyaltySchemeId}` },
        { type: 'MemberImport', id: 'LIST' },
      ],
    }),

    // ── Points catch-up (Stampede staff only) ──────────────────────────────
    // Backfills points-for-spend for paid Square orders missed during an
    // outage. Preview (dry run) and apply both start a background job and
    // return its batch id; poll getPointsCatchupBatch for progress and the
    // result. An applied batch can be undone.
    previewPointsCatchup: build.mutation<
      PointsCatchupJobStarted,
      PointsCatchupScope & { loyaltySchemeId: string }
    >({
      query: ({ loyaltySchemeId, ...scope }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/points-catchup/preview`,
        method: 'post',
        data: scope,
      }),
    }),
    applyPointsCatchup: build.mutation<
      PointsCatchupJobStarted,
      ApplyPointsCatchupBody & { loyaltySchemeId: string; batchId: string }
    >({
      query: ({ loyaltySchemeId, batchId, ...body }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/points-catchup/${batchId}/apply`,
        method: 'post',
        data: body,
      }),
      // Refetch the batch so polling picks up APPLYING straight away.
      invalidatesTags: (_result, _error, { batchId }) => [
        { type: 'PointsCatchup', id: batchId },
      ],
    }),
    undoPointsCatchup: build.mutation<
      CatchupUndoResult,
      { loyaltySchemeId: string; batchId: string }
    >({
      query: ({ loyaltySchemeId, batchId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/points-catchup/${batchId}/undo`,
        method: 'post',
        timeout: 1000 * 60 * 5,
      }),
      invalidatesTags: (_result, _error, { loyaltySchemeId, batchId }) => [
        { type: 'PointsCatchup', id: batchId },
        { type: 'Loyalty', id: loyaltySchemeId },
        'LoyaltyUser',
      ],
    }),
    getPointsCatchupBatch: build.query<
      PointsCatchupBatch,
      { loyaltySchemeId: string; batchId: string }
    >({
      query: ({ loyaltySchemeId, batchId }) => ({
        url: `/v1/loyalty-schemes/${loyaltySchemeId}/points-catchup/${batchId}`,
        method: 'get',
      }),
      providesTags: (_result, _error, { batchId }) => [
        { type: 'PointsCatchup', id: batchId },
      ],
    }),
  }),
})

export default loyaltyApi
